/**
 * One routing decision for records opened outside a domain desk.
 *
 * A CRM opportunity is deliberately a desk item: the pipeline owns its peek
 * ceremony. Every full record, including parties, belongs to the canonical
 * entity route. Keeping this as a value (rather than performing navigation)
 * lets every host use TanStack Router's typed `navigate` function unchanged.
 */
export type RecordDestination =
  | { to: '/crm'; search: { peek: string } }
  | { to: '/e/$entity/$id'; params: { entity: string; id: string } }

export function recordDestination(entity: string, id: string): RecordDestination {
  return entity === 'crm_opportunity'
    ? { to: '/crm', search: { peek: id } }
    : { to: '/e/$entity/$id', params: { entity, id } }
}

/**
 * Where an OLD `/crm/<entity>/<id>` bookmark lands (#2267 route cutover).
 *
 * The same one decision, plus the query the person arrived with. A bookmark is
 * somebody's saved state — a filter, a tab, a `?peek=` — and dropping it turns a
 * compatibility redirect into a silent reset. So the incoming search travels,
 * and the destination's own search wins where they name the same key, because
 * the destination's is what makes the URL mean the right record at all.
 *
 * Pure, so the property can be proven without a router: an old URL never
 * resolves to a second renderer, and never loses the id it was bookmarked for.
 */
export function legacyRecordRedirect(
  entity: string, id: string, search: Record<string, unknown> = {},
): { to: RecordDestination['to']; params?: { entity: string; id: string }; search: Record<string, unknown> } {
  const destination = recordDestination(entity, id)
  return destination.to === '/crm'
    ? { to: '/crm', search: { ...search, ...destination.search } }
    : { to: '/e/$entity/$id', params: destination.params, search }
}
